import React, { useState } from 'react';
import { router, Head, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Plug, Plus, BookOpen, AlertTriangle, AlertCircle } from 'lucide-react';
import AssignWorkersModal from '@/frontend/components/Integrations/AssignWorkersModal';
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip';
import PostConnectionModal from '@/frontend/components/PostConnectionModal';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import AssignedWorkersBanner from '@/frontend/components/Integrations/AssignedWorkersBanner';
import { BackLink } from '@/frontend/components/BackLink';
import type { NotionIntegration, AssignWorker } from './types';

interface Props {
  page_title: string;
  notion_connected: boolean;
  notion_integrations: NotionIntegration[];
  digital_workers: AssignWorker[];
}

const NotionPage = ({
  page_title,
  notion_connected,
  notion_integrations = [],
  digital_workers = [],
}: Props) => {
  const { can_manage_resources } = usePermissions();
  const { flash } = usePage<{ flash: { assign_workers?: boolean } }>().props;
  const [assignWorkersOpen, setAssignWorkersOpen] = useState(
    () => !!flash?.assign_workers && digital_workers.length > 0
  );
  const [savingWorkerAssignment, setSavingWorkerAssignment] = useState(false);
  const justConnected = useJustConnectedWorker(digital_workers);

  const handleToggleStatus = (integrationId: string) => {
    router.patch(`/dashboard/integrations/toggle_notion_status/${integrationId}`, {}, {
      preserveScroll: true,
    });
  };

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6">
        <div className="space-y-6 min-h-full">
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" />

          {/* Header único (padrão da ficha de credencial): quadrado do ícone +
              nome + chip de status + ações. O "brilho" gradiente, o selo de
              conectado e o título central duplicado saíram. */}
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              <BookOpen className="text-muted-foreground" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">Notion</h1>
                <ConnectionStatusChip connected={notion_connected} />
              </div>
            </div>
            {can_manage_resources && (
              <div className="flex shrink-0 items-center gap-2">
                <a href="/dashboard/integrations/connect_notion">
                  <Button variant="outline" size="sm">
                    {notion_connected ? <Plus className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
                    <span className="hidden sm:inline">{notion_connected ? 'Adicionar conta' : 'Conectar'}</span>
                    <span className="sm:hidden">{notion_connected ? 'Adicionar' : 'Conectar'}</span>
                  </Button>
                </a>
              </div>
            )}
          </div>

          {!can_manage_resources && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
              <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
            </Alert>
          )}

          <Card><CardContent className="p-6">
            {notion_integrations.length === 0 ? (
              <p className="text-[13px] text-muted-foreground">
                Conecte sua conta do Notion para sincronizar dados e automatizar fluxos de trabalho.
              </p>
            ) : (
              <div className="space-y-6">
                {notion_integrations.map((integration) => {
                  const isActive = integration.status === 'active';
                  const isError = integration.status === 'error' || integration.status === 'expired';

                  return (
                    <div key={integration.id} className="space-y-4">
                      <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-[13px] font-medium text-foreground">
                              {integration.workspace_name || 'Workspace'}
                            </p>
                            {integration.owner_email && (
                              <p className="text-[11.5px] text-muted-foreground">{integration.owner_email}</p>
                            )}
                            <p className="text-[11.5px] text-muted-foreground">
                              Conectado em {new Date(integration.connected_at).toLocaleDateString('pt-BR')}
                              {' · Por: '}{integration.connected_by || 'N/A'}
                            </p>
                          </div>
                          {can_manage_resources && (
                            <div className="flex items-center gap-2">
                              <a href="/dashboard/integrations/connect_notion">
                                <Button variant="outline" size="sm">
                                  <span className="hidden sm:inline">Reconectar</span>
                                  <span className="sm:hidden">Recon.</span>
                                </Button>
                              </a>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                                onClick={() => {
                                  if (confirm('Tem certeza que deseja desconectar esta conta do Notion?')) {
                                    router.delete(`/dashboard/integrations/disconnect_notion/${integration.id}`, {
                                      preserveScroll: true
                                    });
                                  }
                                }}
                              >
                                <span className="hidden sm:inline">Desconectar</span>
                                <span className="sm:hidden">Desc.</span>
                              </Button>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label htmlFor={`notion-toggle-${integration.id}`}>Habilitar integração</Label>
                            <p className="text-[11.5px] text-muted-foreground">{`Todos os membros podem usar esta integração (conectado por ${integration.connected_by || 'N/A'})`}</p>
                          </div>
                          <Switch
                            id={`notion-toggle-${integration.id}`}
                            checked={isActive}
                            onCheckedChange={() => handleToggleStatus(integration.id)}
                            disabled={!can_manage_resources || isError}
                          />
                        </div>
                      </div>

                      {isError && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Erro na integração</AlertTitle>
                          <AlertDescription>Esta integração está com problemas. Tente reconectar para resolver.</AlertDescription>
                        </Alert>
                      )}

                      {!isError && Object.keys(integration.permissions).length === 0 && (
                        <Alert variant="warning">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>Permissões não configuradas</AlertTitle>
                          <AlertDescription>Esta integração foi conectada antes do sistema de permissões. Reconecte para obter acesso às novas funcionalidades.</AlertDescription>
                        </Alert>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent></Card>

          <AssignedWorkersBanner
            workers={digital_workers.filter((w) => w.assigned)}
            onAddWorker={() => setAssignWorkersOpen(true)}
            onRemoveWorker={(workerId) => router.delete('/dashboard/integrations/remove_worker', { data: { provider: 'notion', worker_id: workerId }, preserveScroll: true })}
          />
        </div>
      </div>

      <AssignWorkersModal
        open={assignWorkersOpen}
        onClose={() => setAssignWorkersOpen(false)}
        providerLabel="Notion"
        workers={digital_workers}
        provider="notion"
        isNotion
        saving={savingWorkerAssignment}
        onSavingChange={setSavingWorkerAssignment}
      />

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context="Notion"
        />
      )}
    </>
  );
};

NotionPage.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default NotionPage;
